import {createContext, useState, useCallback, Dispatch, useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import utils from 'utils';
import Loader from '@/components/Loader';

/**
 * getLangFromPath - extracts language code from a URL pathname
 * @param {string} pathname
 * @returns {string}
 */
const getLangFromPath = (pathname: string): string => {
    const supported = ['sk', 'en'];
    const first = pathname.split('/').filter(Boolean)[0];
    return supported.includes(first) ? first : 'sk';
};

interface IAppContext {
    user: User;
    setUser: Dispatch<User>;
    logOut: () => void;
    requestData: RequestData;
    setRequestData: Dispatch<RequestData>;
    popups: Popup[];
    castPopup: (popup: Popup) => string;
    closePopup: (id: string) => void;
    defs: Defs;
    activeTab: string;
    shipmentInfo: ShipmentInfo,
    setShipmentInfo: Dispatch<ShipmentInfo>,
    load: boolean,
    setLoad: SetLoad,
    shipmentNr: string,
    setShipmentNr: Dispatch<ShipmentNr>,
    lang: string,
    setLang: Dispatch<string>,
}

const defaultState: IAppContext = {
    user: undefined,
    setUser: () => {},
    logOut: () => {},
    requestData: utils.dataConstructors.RequestData,
    setRequestData: () => {},
    popups: [],
    castPopup: () => '',
    closePopup: () => {},
    defs: utils.dataConstructors.Defs,
    activeTab: '',
    shipmentInfo: utils.dataConstructors.ShipmentInfo,
    setShipmentInfo: () => {},
    load: false,
    setLoad: () => false,
    shipmentNr: '',
    setShipmentNr: () => '',
    lang: 'sk',
    setLang: () => {},
};

export const AppContext = createContext<IAppContext | null>(defaultState);

export const AppContextProvider = (props: any) => {
    // states of Context
    const [user, setUser] = useState<User>(undefined);
    const [defs, setDefs] = useState<Defs>(utils.dataConstructors.Defs);
    const [popups, setPopups] = useState<Popup[]>([]);
    const [requestData, setRequestData] = useState<RequestData>(utils.dataConstructors.RequestData);
    const [shipmentInfo, setShipmentInfo] = useState<ShipmentInfo>(utils.dataConstructors.ShipmentInfo);
    const [shipmentNr, setShipmentNr] = useState<string>('');
    const [activeTab, setActiveTab] = useState<string>('');
    const [load, setLoad] = useState<boolean>(false);
    const [lang, setLang] = useState<string>(() => getLangFromPath(window.location.pathname));

    const setShipmentNrTrim = useCallback((shipmentNrValue: string): void => {
        setShipmentNr(shipmentNrValue?.trim?.() || shipmentNrValue);
    }, [setShipmentNr]);

    // functions of Context (only functions that interact with states others should be in utils)
    /**
     * logOut - log out user
     */
    const logOut = () => {
        if (user?.type === 'internal') {
            utils.setStorage('internalUser', {});
            utils.setStorage('internalUserToken', '');
        }

        setUser(undefined);
        // redirect to homepage
        window.location.href = `/${utils.getLanguage()}`;
    };

    /**
     * castPopup - cast popup
     * @param {Popup} popup
     * @returns {string} id of popup
     */
    const castPopup = useCallback((popup: Popup) => {
        const newPopups = [...popups];
        const uuid = utils.generateUuid();
        newPopups.push({
            ...popup,
            id: uuid,
        });
        setPopups(newPopups);
        return uuid;
    }, [popups, setPopups]);

    /**
     * closePopup - close popup
     * @param {string} id
     */
    const closePopup = useCallback((id: string) => {
        const newPopups = popups.filter((popup) => popup.id !== id);
        setPopups(newPopups);
    }, [popups, setPopups]);

    // query data
    const queryDefs = useCallback(async () => {
        const response = await utils.fetch('ui/defs');
        return response;
    }, []);

    const queryDefsQuery = useQuery({queryKey: ['defs'], queryFn: queryDefs});

    useEffect(() => {
        if (!queryDefsQuery.isLoading && queryDefsQuery.isError) {
            console.error('queryDefsQuery error', queryDefsQuery.error);
            return;
        }

        if (!queryDefsQuery.isLoading && queryDefsQuery.data) {
            const defs = queryDefsQuery.data;
            // strip lang prefix before matching menu item links (e.g. /en/request → /request)
            const moduleFromUrl = window.location.pathname.replace(`/${lang}`, '') || '/';
            defs.menu = defs.menu.map((menuItem) => {
                if (menuItem.link === moduleFromUrl) {
                    menuItem.active = true;
                    setActiveTab(menuItem.link);
                } else {
                    menuItem.active = false;
                }
                return menuItem;
            });
            setDefs(queryDefsQuery.data);
        }

        // load internal user from storage if exists
        const userStore = utils.getStorage('internalUser');
        if (userStore && !user) {
            setUser(userStore);
        }
    }, [queryDefsQuery, queryDefsQuery.data, user, setUser, setDefs, lang]);

    utils.useLoadLangs(lang);
    utils.useLoadRegexes();
    const langQuery = utils.useTranslate();
    const isLangLoaded = !!langQuery?.isLangLoaded?.();
    const bootLoading = !isLangLoaded;
    const showGlobalLoader = load || bootLoading;

    // exported values of Context
    const contextValue = {
        user,
        setUser,
        logOut,
        requestData,
        setRequestData,
        popups,
        castPopup,
        closePopup,
        defs,
        activeTab,
        shipmentInfo,
        setShipmentInfo,
        load,
        setLoad,
        shipmentNr,
        setShipmentNr: setShipmentNrTrim,
        lang,
        setLang,
    };

    return (
        <AppContext.Provider value={contextValue}>
            <Loader visible={showGlobalLoader} opaque={bootLoading} />
            {isLangLoaded ? props.children : null}
        </AppContext.Provider>
    );
};

export default AppContext;
